import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processOAuthCallback = async () => {
      const sessionId = searchParams.get('session_id');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage('Google authentication was cancelled or failed.');
        return;
      }

      if (!sessionId) {
        setStatus('error');
        setErrorMessage('Invalid authentication response.');
        return;
      }

      try {
        // Send session to backend to get user data and JWT
        const response = await api.post('/auth/google/session', {}, {
          headers: {
            'X-Session-ID': sessionId
          }
        });

        const { access_token, user } = response.data;

        // Store token
        localStorage.setItem('token', access_token);
        
        // Update auth context
        setUser(user);

        setStatus('success');
        toast.success(`Welcome, ${user.first_name || user.email}!`);

        // Redirect based on role
        setTimeout(() => {
          if (user.role === 'admin' || user.role === 'sub_admin' || user.role === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/client/dashboard');
          }
        }, 1500);

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setErrorMessage(err.response?.data?.detail || 'Failed to complete authentication.');
        toast.error('Authentication failed');
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="glass rounded-2xl p-8 text-center animate-fade-in">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Signing you in...</h2>
              <p className="text-gray-600">Please wait while we complete your authentication.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
              <p className="text-gray-600">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-medium rounded-lg"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
